import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Store, Sparkles, LifeBuoy, ShoppingBag } from 'lucide-react';
import { useApp } from '@/state/AppContext';
import { classNames } from '@/lib/format';

const items = [
  { to: '/', label: 'Home', icon: Home, test: 'bottom-nav-home', exact: true },
  { to: '/shop', label: 'Shop', icon: Store, test: 'bottom-nav-shop' },
  { to: '/customize', label: 'Customize', icon: Sparkles, test: 'bottom-nav-customize' },
  { to: '/support', label: 'Support', icon: LifeBuoy, test: 'bottom-nav-support' },
  { to: '/cart', label: 'Cart', icon: ShoppingBag, test: 'bottom-nav-cart' },
];

export const BottomNav = () => {
  const { cartCount } = useApp();
  const { pathname, search } = useLocation();
  // Trigger cart-icon bump animation when cart count increases
  const prevCountRef = useRef(cartCount);
  const [bumpKey, setBumpKey] = useState(0);
  useEffect(() => {
    if (cartCount > prevCountRef.current) setBumpKey((k) => k + 1);
    prevCountRef.current = cartCount;
  }, [cartCount]);

  const isActive = (to, exact) => {
    const [base, q] = to.split('?');
    if (exact) return pathname === base && !search;
    if (q) {
      const want = new URLSearchParams(q);
      const have = new URLSearchParams(search);
      for (const [k, v] of want.entries()) if (have.get(k) !== v) return false;
      return pathname === base;
    }
    return pathname === base || pathname.startsWith(base + '/');
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[var(--bb-darkblue)]/10 md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="bottom-nav"
    >
      <ul className="grid grid-cols-5 h-16 max-w-6xl mx-auto">
        {items.map(({ to, label, icon: Icon, test, exact }) => {
          const active = isActive(to, exact);
          const isCart = to === '/cart';
          return (
            <li key={to}>
              <NavLink
                to={to}
                className={classNames(
                  'h-full flex flex-col items-center justify-center gap-1 text-[11px] font-nunito-bold transition-colors active:scale-95 transition-transform duration-150',
                  active ? 'text-[var(--bb-cyan)]' : 'text-[var(--bb-darkblue)] hover:text-[var(--bb-cyan)]',
                )}
                data-testid={test}
              >
                <span className="relative">
                  <Icon
                    key={isCart ? `cart-${bumpKey}` : undefined}
                    className={classNames(
                      'w-5 h-5 transition-transform duration-200',
                      active ? 'scale-110' : 'scale-100',
                      isCart && bumpKey > 0 ? 'bb-anim-cart-bump' : '',
                    )}
                  />
                  {isCart && cartCount > 0 && (
                    <span
                      key={`badge-${bumpKey}`}
                      className="absolute -top-1.5 -right-2 min-w-4 h-4 px-1 rounded-full bg-[var(--bb-pink)] text-white text-[10px] font-extrabold flex items-center justify-center bb-anim-bounce-in"
                    >
                      {cartCount}
                    </span>
                  )}
                </span>
                <span>{label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
